/**
 * Minimal client-side path router — no react-router dependency, matching
 * this app's other hand-rolled-over-library choices (see cognitoAuth.ts).
 * Three static routes don't need more than "URL <-> feature id" plus
 * History API push/replace.
 */
import { useSyncExternalStore } from "react";

type Listener = () => void;

const listeners = new Set<Listener>();

function subscribe(listener: Listener): () => void {
  window.addEventListener("popstate", listener);
  listeners.add(listener);
  return () => {
    window.removeEventListener("popstate", listener);
    listeners.delete(listener);
  };
}

function getSnapshot(): string {
  return window.location.pathname;
}

/** The current pathname, reactive to both back/forward navigation and `navigate()` below. */
export function useRoute(): string {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * Changes the URL client-side and notifies every `useRoute()` subscriber.
 * `replace: true` swaps the current history entry instead of pushing a new
 * one — used for normalizing an unknown path and for restoring the
 * pre-login page after the Cognito redirect (see cognitoAuth.ts).
 *
 * A pushed navigation is a new page, so it starts at the top, as a full page
 * load would; otherwise picking a tab halfway down one page lands halfway
 * down the next. `replace` keeps the scroll position because both of its
 * callers run on load. Back/forward never come through here (popstate), so
 * the browser's own scroll restoration still handles those.
 */
export function navigate(path: string, options: { replace?: boolean } = {}): void {
  if (options.replace) {
    // Always applied, even if `path` matches the current pathname: the
    // auth callback (see cognitoAuth.ts) uses `replace` to strip a
    // `?code=&state=` query string that `pathname` alone wouldn't reflect.
    window.history.replaceState({}, "", path);
  } else {
    if (path === window.location.pathname) return;
    window.history.pushState({}, "", path);
    window.scrollTo(0, 0);
  }
  listeners.forEach((listener) => listener());
}
